import type { DayPlan, MealSlot, Macros } from '@/types/models'

export const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
export const DAY_LABELS_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

export const SLOT_TYPE_LABELS: Record<string, string> = {
  meal: 'Meal',
  snack: 'Snack',
  beverage: 'Beverage',
}

export const DEFAULT_MEAL_LABELS = ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Second Dinner']

export function getMealLabel(slotIndex: number, slotType: string, label: string | null): string {
  if (label) return label
  if (slotType === 'snack') return `Snack`
  if (slotType === 'beverage') return `Beverage`
  return DEFAULT_MEAL_LABELS[slotIndex] ?? `Meal ${slotIndex + 1}`
}

export function sumDayMacros(slots: MealSlot[]): Partial<Macros> {
  const result = { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0 }
  for (const s of slots) {
    if (s.is_skipped) continue
    result.calories += s.total_calories ?? 0
    result.protein_g += s.total_protein_g ?? 0
    result.carbs_g += s.total_carbs_g ?? 0
    result.fat_g += s.total_fat_g ?? 0
    result.fiber_g += s.total_fiber_g ?? 0
  }
  return result
}

export function sumWeekMacros(days: DayPlan[]): Partial<Macros> {
  const result = { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0 }
  for (const day of days) {
    const dm = sumDayMacros(day.slots ?? [])
    result.calories += dm.calories ?? 0
    result.protein_g += dm.protein_g ?? 0
    result.carbs_g += dm.carbs_g ?? 0
    result.fat_g += dm.fat_g ?? 0
    result.fiber_g += dm.fiber_g ?? 0
  }
  return result
}

export function macroPercent(value: number, target: number | null): number {
  if (!target || target === 0) return 0
  return Math.min(100, Math.round((value / target) * 100))
}

export function formatMacro(value: number | null, unit = 'g'): string {
  if (value === null || value === undefined) return '—'
  return unit === 'kcal'
    ? `${Math.round(value)} kcal`
    : `${Math.round(value)}${unit}`
}

export function getWeekStartDate(offset = 0): string {
  const now = new Date()
  const day = now.getDay() // 0=Sun
  const monday = new Date(now)
  monday.setDate(now.getDate() - ((day + 6) % 7) + offset * 7)
  return monday.toISOString().split('T')[0]
}

export function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export const STORE_TYPE_LABELS: Record<string, string> = {
  grocery: 'Grocery',
  asian_grocery: 'Asian Grocery',
  warehouse: 'Warehouse Store',
  specialty: 'Specialty',
  butcher: 'Butcher',
  farmers_market: "Farmer's Market",
  custom: 'Other',
}

export const DEPARTMENT_ORDER = [
  'produce',
  'meat_seafood',
  'dairy_eggs',
  'bakery',
  'dry_goods',
  'canned_goods',
  'frozen',
  'condiments_sauces',
  'beverages',
  'pantry_staples',
  'other',
]

export const DEPARTMENT_LABELS: Record<string, string> = {
  produce: '🥦 Produce',
  meat_seafood: '🥩 Meat & Seafood',
  dairy_eggs: '🥛 Dairy & Eggs',
  bakery: '🍞 Bakery',
  dry_goods: '🌾 Dry Goods & Grains',
  canned_goods: '🥫 Canned Goods',
  frozen: '🧊 Frozen',
  condiments_sauces: '🫙 Condiments & Sauces',
  beverages: '🧃 Beverages',
  pantry_staples: '🧂 Pantry Staples',
  other: '📦 Other',
}
